import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { format, subMonths, startOfMonth, endOfMonth } from "date-fns";
import { es } from "date-fns/locale";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Input,
  Badge,
  Skeleton,
} from "@learn-me/ui";
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  ShoppingCart,
  RotateCcw,
  Download,
  Calendar,
  Search,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import {
  adminApi,
  type FinanceAnalytics,
  type Transaction,
} from "@/features/admin/api/adminApi";
import { SalesChart } from "@/features/admin/components/finance/SalesChart";
import { TopCoursesChart } from "@/features/admin/components/finance/TopCoursesChart";
import { CurrencyDistributionChart } from "@/features/admin/components/finance/CurrencyDistributionChart";

type PeriodOption =
  | "this_month"
  | "last_month"
  | "last_3_months"
  | "last_6_months"
  | "this_year"
  | "custom";

function getPeriodDates(period: PeriodOption): {
  startDate: string;
  endDate: string;
} {
  const now = new Date();
  let startDate: Date;
  let endDate: Date = now;

  switch (period) {
    case "this_month":
      startDate = startOfMonth(now);
      endDate = endOfMonth(now);
      break;
    case "last_month":
      startDate = startOfMonth(subMonths(now, 1));
      endDate = endOfMonth(subMonths(now, 1));
      break;
    case "last_3_months":
      startDate = startOfMonth(subMonths(now, 2));
      break;
    case "last_6_months":
      startDate = startOfMonth(subMonths(now, 5));
      break;
    case "this_year":
      startDate = new Date(now.getFullYear(), 0, 1);
      break;
    default:
      startDate = startOfMonth(now);
  }

  return {
    startDate: startDate.toISOString(),
    endDate: endDate.toISOString(),
  };
}

function formatCurrency(amount: number, currency = "USD"): string {
  return new Intl.NumberFormat("es-CR", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
  }).format(amount);
}

function KPICard({
  title,
  value,
  subtitle,
  change,
  icon: Icon,
  isLoading,
}: {
  title: string;
  value: string;
  subtitle?: string;
  change?: number;
  icon: React.ComponentType<{ className?: string }>;
  isLoading?: boolean;
}) {
  if (isLoading) {
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-4 w-4" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-8 w-32 mb-1" />
          <Skeleton className="h-3 w-20" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">
          {title}
        </CardTitle>
        <Icon className="h-4 w-4 text-muted-foreground" />
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{value}</div>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          {subtitle && <span>{subtitle}</span>}
          {change !== undefined && (
            <span
              className={`flex items-center ${
                change >= 0 ? "text-green-600" : "text-red-600"
              }`}
            >
              {change >= 0 ? (
                <TrendingUp className="h-3 w-3 mr-1" />
              ) : (
                <TrendingDown className="h-3 w-3 mr-1" />
              )}
              {change >= 0 ? "+" : ""}
              {change.toFixed(1)}%
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function StatusBadge({ status }: { status: string }) {
  const statusConfig: Record<
    string,
    {
      label: string;
      variant: "default" | "secondary" | "destructive" | "outline";
    }
  > = {
    COMPLETED: { label: "Completado", variant: "default" },
    PENDING: { label: "Pendiente", variant: "secondary" },
    FAILED: { label: "Fallido", variant: "destructive" },
    REFUNDED: { label: "Reembolsado", variant: "outline" },
  };

  const config = statusConfig[status] || {
    label: status,
    variant: "secondary" as const,
  };

  return <Badge variant={config.variant}>{config.label}</Badge>;
}

export default function FinancePage() {
  const [period, setPeriod] = useState<PeriodOption>("this_year");
  const [currency, setCurrency] = useState<"USD" | "CRC" | "ALL">("ALL");
  const [transactionsPage, setTransactionsPage] = useState(1);
  const [transactionSearch, setTransactionSearch] = useState("");
  const [transactionStatus, setTransactionStatus] = useState<string>("ALL");

  const { startDate, endDate } = getPeriodDates(period);

  // Solo comparar con período anterior para rangos cortos (evita timeouts)
  const shouldCompare = period === "this_month" || period === "last_month";

  // Fetch analytics
  const { data: analytics, isLoading: analyticsLoading } = useQuery({
    queryKey: [
      "finance-analytics",
      startDate,
      endDate,
      currency,
      shouldCompare,
    ],
    queryFn: () =>
      adminApi.getFinanceAnalytics({
        startDate,
        endDate,
        currency: currency === "ALL" ? undefined : currency,
        compareWithPrevious: shouldCompare,
      }),
  });

  // Fetch transactions
  const { data: transactionsData, isLoading: transactionsLoading } = useQuery({
    queryKey: [
      "finance-transactions",
      transactionsPage,
      transactionSearch,
      transactionStatus,
      startDate,
      endDate,
      currency,
    ],
    queryFn: () =>
      adminApi.getFinanceTransactions(transactionsPage, 10, {
        search: transactionSearch || undefined,
        status: transactionStatus === "ALL" ? undefined : transactionStatus,
        currency: currency === "ALL" ? undefined : currency,
        startDate,
        endDate,
      }),
  });

  const handleExport = async (type: "summary" | "transactions") => {
    try {
      const blob = await adminApi.exportFinanceData(type, {
        startDate,
        endDate,
        currency: currency === "ALL" ? undefined : currency,
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `finance-${type}-${format(new Date(), "yyyy-MM-dd")}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error("Error exporting:", error);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Finanzas</h1>
          <p className="text-muted-foreground">
            Analítica financiera y métricas de ventas
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select
            value={period}
            onValueChange={(v: string) => setPeriod(v as PeriodOption)}
          >
            <SelectTrigger className="w-[180px]">
              <Calendar className="h-4 w-4 mr-2" />
              <SelectValue placeholder="Período" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="this_month">Este mes</SelectItem>
              <SelectItem value="last_month">Mes anterior</SelectItem>
              <SelectItem value="last_3_months">Últimos 3 meses</SelectItem>
              <SelectItem value="last_6_months">Últimos 6 meses</SelectItem>
              <SelectItem value="this_year">Este año</SelectItem>
            </SelectContent>
          </Select>
          <Select
            value={currency}
            onValueChange={(v: string) =>
              setCurrency(v as "USD" | "CRC" | "ALL")
            }
          >
            <SelectTrigger className="w-[120px]">
              <DollarSign className="h-4 w-4 mr-2" />
              <SelectValue placeholder="Moneda" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">Todas</SelectItem>
              <SelectItem value="USD">USD</SelectItem>
              <SelectItem value="CRC">CRC</SelectItem>
            </SelectContent>
          </Select>
          <Select
            onValueChange={(v: string) =>
              handleExport(v as "summary" | "transactions")
            }
          >
            <SelectTrigger className="w-[140px]">
              <Download className="h-4 w-4 mr-2" />
              <SelectValue placeholder="Exportar" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="summary">Resumen CSV</SelectItem>
              <SelectItem value="transactions">Transacciones CSV</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
        <KPICard
          title="Ingresos Totales"
          value={formatCurrency(analytics?.kpis.totalRevenue || 0)}
          subtitle={`${analytics?.kpis.totalSales || 0} ventas`}
          change={analytics?.kpis.comparison?.revenueChange}
          icon={DollarSign}
          isLoading={analyticsLoading}
        />
        <KPICard
          title="Ingresos del Mes"
          value={formatCurrency(analytics?.kpis.revenueThisMonth || 0)}
          subtitle={`${analytics?.kpis.salesThisMonth || 0} ventas`}
          icon={TrendingUp}
          isLoading={analyticsLoading}
        />
        <KPICard
          title="Ventas Totales"
          value={String(analytics?.kpis.totalSales || 0)}
          change={analytics?.kpis.comparison?.salesChange}
          icon={ShoppingCart}
          isLoading={analyticsLoading}
        />
        <KPICard
          title="Tasa de Reembolso"
          value={`${(analytics?.kpis.refundRate || 0).toFixed(1)}%`}
          subtitle={`${analytics?.kpis.refundCount || 0} reembolsos`}
          icon={RotateCcw}
          isLoading={analyticsLoading}
        />
        <KPICard
          title="Ticket Promedio"
          value={formatCurrency(analytics?.kpis.averageOrderValue || 0)}
          change={analytics?.kpis.comparison?.aovChange}
          icon={DollarSign}
          isLoading={analyticsLoading}
        />
      </div>

      {/* Charts Row */}
      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Ventas por Mes</CardTitle>
            <CardDescription>
              Ingresos y cantidad de ventas mensuales
            </CardDescription>
          </CardHeader>
          <CardContent>
            <SalesChart
              data={analytics?.monthlySales || []}
              isLoading={analyticsLoading}
            />
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Distribución por Moneda</CardTitle>
            <CardDescription>Porcentaje de ventas por moneda</CardDescription>
          </CardHeader>
          <CardContent>
            <CurrencyDistributionChart
              data={analytics?.currencyDistribution || []}
              isLoading={analyticsLoading}
            />
          </CardContent>
        </Card>
      </div>

      {/* Top Courses */}
      <Card>
        <CardHeader>
          <CardTitle>Top Cursos por Ingresos</CardTitle>
          <CardDescription>
            Los cursos con mayores ventas en el período seleccionado
          </CardDescription>
        </CardHeader>
        <CardContent>
          <TopCoursesChart
            data={analytics?.topCourses || []}
            isLoading={analyticsLoading}
          />
        </CardContent>
      </Card>

      {/* Transactions Table */}
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <CardTitle>Transacciones</CardTitle>
              <CardDescription>
                Historial de pagos y transacciones
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar..."
                  className="pl-9 w-[200px]"
                  value={transactionSearch}
                  onChange={(e) => {
                    setTransactionSearch(e.target.value);
                    setTransactionsPage(1);
                  }}
                />
              </div>
              <Select
                value={transactionStatus}
                onValueChange={(v: string) => {
                  setTransactionStatus(v);
                  setTransactionsPage(1);
                }}
              >
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder="Estado" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">Todos</SelectItem>
                  <SelectItem value="COMPLETED">Completados</SelectItem>
                  <SelectItem value="PENDING">Pendientes</SelectItem>
                  <SelectItem value="FAILED">Fallidos</SelectItem>
                  <SelectItem value="REFUNDED">Reembolsados</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {transactionsLoading ? (
            <div className="space-y-3">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : (
            <>
              <div className="rounded-md border">
                <table className="w-full">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="py-3 px-4 text-left text-sm font-medium">
                        Usuario
                      </th>
                      <th className="py-3 px-4 text-left text-sm font-medium">
                        Curso
                      </th>
                      <th className="py-3 px-4 text-right text-sm font-medium">
                        Monto
                      </th>
                      <th className="py-3 px-4 text-center text-sm font-medium">
                        Estado
                      </th>
                      <th className="py-3 px-4 text-right text-sm font-medium">
                        Fecha
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactionsData?.payments.map((tx: Transaction) => (
                      <tr key={tx.id} className="border-b last:border-0">
                        <td className="py-3 px-4">
                          <div className="font-medium text-sm">
                            {tx.user.name}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {tx.user.email}
                          </div>
                        </td>
                        <td className="py-3 px-4 text-sm">
                          {tx.course?.title || "N/A"}
                        </td>
                        <td className="py-3 px-4 text-right text-sm font-medium">
                          {formatCurrency(tx.amount, tx.currency)}
                        </td>
                        <td className="py-3 px-4 text-center">
                          <StatusBadge status={tx.status} />
                        </td>
                        <td className="py-3 px-4 text-right text-sm text-muted-foreground">
                          {format(new Date(tx.createdAt), "dd MMM yyyy", {
                            locale: es,
                          })}
                        </td>
                      </tr>
                    ))}
                    {transactionsData?.payments.length === 0 && (
                      <tr>
                        <td
                          colSpan={5}
                          className="py-8 text-center text-muted-foreground"
                        >
                          No se encontraron transacciones
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              {/* Pagination */}
              {transactionsData &&
                transactionsData.pagination.totalPages > 1 && (
                  <div className="flex items-center justify-between mt-4">
                    <p className="text-sm text-muted-foreground">
                      Mostrando {(transactionsPage - 1) * 10 + 1} -{" "}
                      {Math.min(
                        transactionsPage * 10,
                        transactionsData.pagination.total,
                      )}{" "}
                      de {transactionsData.pagination.total}
                    </p>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={transactionsPage <= 1}
                        onClick={() => setTransactionsPage((p) => p - 1)}
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      <span className="text-sm">
                        Página {transactionsPage} de{" "}
                        {transactionsData.pagination.totalPages}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={
                          transactionsPage >=
                          transactionsData.pagination.totalPages
                        }
                        onClick={() => setTransactionsPage((p) => p + 1)}
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
